import React, { useState, useEffect } from 'react';
import { Table } from 'antd';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { api_post } from '../../helpers';
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { Link } from "react-router-dom";
import { ChevronUp, RotateCcw } from "feather-icons-react/build/IconComponents";
import { useDispatch, useSelector } from "react-redux";
import { setToogleHeader } from "../../core/redux/action";

const SpinHistory = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const headerToggle = useSelector((state) => state.toggle_header);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [searchKeyword, setSearchKeyword] = useState("");
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
    showSizeChanger: true,
    showQuickJumper: true,
    showTotal: (total) => `Total ${total} items`,
    pageSizeOptions: ['10', '20', '50', '100'],
  });

  const columns = [
    {
      title: 'User Name',
      dataIndex: 'user_name',
      key: 'user_name',
      sorter: (a, b) => a.user_name.localeCompare(b.user_name),
    },
    {
      title: 'User ID',
      dataIndex: 'user_id',
      key: 'user_id',
      sorter: (a, b) => a.user_id - b.user_id,
    },
    {
      title: 'Prize Name',
      dataIndex: 'prize_name',
      key: 'prize_name',
      sorter: (a, b) => a.prize_name.localeCompare(b.prize_name),
    },
    {
      title: 'Rank Name',
      dataIndex: 'rank_name',
      key: 'rank_name',
      sorter: (a, b) => a.rank_name.localeCompare(b.rank_name),
    },
    {
      title: 'Won At',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (text) => format(new Date(text), 'yyyy-MM-dd HH:mm:ss'),
      sorter: (a, b) => new Date(a.created_at) - new Date(b.created_at),
    },
  ];

  const fetchData = async (page = 1, pageSize = 10, keyword = "") => {
    try {
      setLoading(true);
      const queryParams = new URLSearchParams();
      queryParams.append('limit', pageSize);
      if (keyword) {
        queryParams.append('keyword', keyword);
      }
      
      const response = await api_post(`spinwheel/spin/history/${page}?${queryParams.toString()}`, {});

      if (response.status === 1) {
        setData(response.data.collection);
        
        // Calculate the total number of items based on total_page and limit
        // For Ant Design's pagination to work correctly, we need to estimate the total number of items
        // If we're on the last page, use the actual items count, otherwise use total_page * limit
        const currentPage = parseInt(response.data.pagination.page, 10);
        const totalPages = parseInt(response.data.pagination.total_page, 10);
        const limit = parseInt(response.data.pagination.limit, 10);
        
        // Calculate total items - if we're on the last page, use the actual count
        // otherwise, estimate based on full pages
        let totalItems;
        if (currentPage === totalPages) {
          // On last page, use the actual count of items we have plus the items on previous pages
          totalItems = response.data.collection.length + (currentPage - 1) * limit;
        } else {
          // Not on last page, so we estimate based on total_page * limit
          totalItems = totalPages * limit;
        }
        
        setPagination({
          ...pagination,
          current: currentPage,
          pageSize: limit,
          total: totalItems,
        });
      } else if (response.system === "no.history.found") {
        // Handle no history found case
        setData([]);
        setPagination({
          ...pagination,
          current: 1,
          total: 0,
        });
      } else {
        console.error('API returned non-success status:', response);
      }
    } catch (error) {
      console.error('Error fetching spin history:', error);
      if (error.response && error.response.status === 401) {
        navigate('/signin');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(1, pagination.pageSize, searchKeyword);
  }, []);

  // Add a separate effect to handle search keyword changes
  useEffect(() => {
    // When search keyword changes, reset to first page
    if (searchKeyword !== "") {
      setPagination({
        ...pagination,
        current: 1
      });
    }
  }, [searchKeyword]);

  const handleTableChange = (newPagination) => {
    // When user changes page or page size, fetch new data
    fetchData(newPagination.current, newPagination.pageSize, searchKeyword);
  };

  const handleSearch = (e) => {
    setSearchKeyword(e.target.value);
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    fetchData(1, pagination.pageSize, searchKeyword);
  };

  const handleRefresh = () => {
    // Reset to first page and fetch data
    fetchData(1, pagination.pageSize, searchKeyword);
  };

  const renderRefreshTooltip = (props) => (
    <Tooltip id="refresh-tooltip" {...props}>
      Refresh
    </Tooltip>
  );

  const renderCollapseTooltip = (props) => (
    <Tooltip id="collapse-tooltip" {...props}>
      Collapse
    </Tooltip>
  );

  return (
    <div className="page-wrapper">
      <div className="content container-fluid">
        <div className="page-header">
          <div className="row align-items-center">
            <div className="col">
              <h3 className="page-title">Spin History</h3>
            </div>
            <div className="col-auto">
              <ul className="table-top-head">
                <li>
                  <OverlayTrigger placement="top" overlay={renderRefreshTooltip}>
                    <Link onClick={handleRefresh}>
                      <RotateCcw />
                    </Link>
                  </OverlayTrigger>
                </li>
                <li>
                  <OverlayTrigger placement="top" overlay={renderCollapseTooltip}>
                    <Link
                      className={headerToggle ? "active" : ""}
                      onClick={() => dispatch(setToogleHeader(!headerToggle))}
                    >
                      <ChevronUp />
                    </Link>
                  </OverlayTrigger>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <div className="row">
          <div className="col-sm-12">
            <div className="card">
              <div className="card-body">
                <div className="table-top">
                  <div className="search-set">
                    <form onSubmit={handleSearchSubmit} className="search-input">
                      <input
                        type="text"
                        placeholder="Search"
                        className="form-control form-control-sm formsearch"
                        value={searchKeyword}
                        onChange={handleSearch}
                      />
                      <button type="submit" className="btn btn-searchset">
                        <i className="feather-search" />
                      </button>
                    </form>
                  </div>
                </div>
                <Table
                  columns={columns}
                  dataSource={data}
                  pagination={pagination}
                  loading={loading}
                  onChange={handleTableChange}
                  rowKey="spin_id"
                  locale={{ emptyText: 'No spin history found' }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SpinHistory; 